import type { z } from 'zod';
import { isApiError } from './api';

/** Messages per field, keyed by the field's path in the request (`value`, `business.phone`). */
export type FieldErrors = Readonly<Record<string, string>>;

export type Parsed<T> = { ok: true; value: T } | { ok: false; errors: FieldErrors };

export const NO_ERRORS: FieldErrors = {};

/** The words an app gives for the mistakes of a form's fields. */
export interface FieldErrorWords {
  /** The contracts' own validation messages, in the app's language. */
  validation: Readonly<Record<string, string>>;
  invalidField: string;
  required: string;
  /** A value another row already uses (a CONFLICT that names a field). */
  taken: string;
}

const FIELD_ERROR_CODES: ReadonlySet<string> = new Set([
  'VALIDATION_FAILED',
  'BAD_REQUEST',
  'CONFLICT',
]);

/** An app's field checks: the contracts' rules and the API's refusals, in the app's words. */
export function createFieldChecks(words: FieldErrorWords) {
  function validationMessage(message: string): string {
    return words.validation[message] ?? words.invalidField;
  }

  function issueMessage(issue: z.core.$ZodIssue): string {
    if (issue.code === 'too_small' && issue.origin === 'string' && Number(issue.minimum) === 1) {
      return words.required;
    }
    return validationMessage(issue.message);
  }

  /** Zod issues (the contracts' own rules, checked before sending) as messages per field. */
  function issuesToFieldErrors(issues: readonly z.core.$ZodIssue[]): FieldErrors {
    const errors: Record<string, string> = {};
    for (const issue of issues) {
      const path = issue.path.map(String).join('.');
      errors[path] ??= issueMessage(issue);
    }
    return errors;
  }

  /**
   * The fields the API rejected: VALIDATION_FAILED and BAD_REQUEST details (`/business/phone`),
   * and CONFLICT details that name a field already in use (a page address another page has:
   * `slug`).
   */
  function serverFieldErrors(error: unknown): FieldErrors {
    if (!isApiError(error) || !FIELD_ERROR_CODES.has(error.code)) return NO_ERRORS;
    const errors: Record<string, string> = {};
    for (const detail of error.details) {
      const path = detail.path.split('/').filter(Boolean).join('.');
      errors[path] ??= error.code === 'CONFLICT' ? words.taken : validationMessage(detail.message);
    }
    return errors;
  }

  /** Checks a request with the contract the API validates it with. */
  function parseWith<Schema extends z.ZodType>(
    schema: Schema,
    input: unknown,
  ): Parsed<z.output<Schema>> {
    const result = schema.safeParse(input);
    return result.success
      ? { ok: true, value: result.data }
      : { ok: false, errors: issuesToFieldErrors(result.error.issues) };
  }

  /** Local (contract) errors first; then what the API rejected. */
  function mergeErrors(local: FieldErrors, apiError: unknown): FieldErrors {
    return { ...serverFieldErrors(apiError), ...local };
  }

  return { issuesToFieldErrors, serverFieldErrors, parseWith, mergeErrors };
}

/** Optional text: trimmed, and null when empty. */
export function textOrNull(value: string): string | null {
  const text = value.trim();
  return text === '' ? null : text;
}
